import type { ErrorCode } from "@mk/shared";
import { errors, unknownErrorMessage } from "../i18n/index.ts";
import { apiOrigin, nativePlatform } from "./native.ts";

/**
 * İnce API istemcisi.
 *
 * Erişim jetonu yalnızca bellekte durur (localStorage'a yazılmaz). Yenileme
 * jetonu httpOnly çerezde olduğu için 401 alındığında sessizce yenilenir.
 */

let accessToken: string | null = null;
let refreshHandler: (() => Promise<string | null>) | null = null;
let refreshing: Promise<string | null> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setRefreshHandler(handler: (() => Promise<string | null>) | null): void {
  refreshHandler = handler;
}

/**
 * Erişim jetonunu yeniler. Aynı anda birden fazla çağrı gelse de tek istek atılır.
 * Soket katmanı da bunu kullanır: jeton süresi dolmuşken yeniden bağlanırken.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshHandler) return Promise.resolve(null);
  refreshing ??= refreshHandler().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ErrorCode | "unknown",
    readonly params?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function readError(res: Response): Promise<{ message: string; code: ErrorCode | "unknown"; params?: unknown }> {
  try {
    const body = (await res.json()) as { error?: unknown; params?: unknown };
    if (typeof body.error === "string") {
      return {
        message: unknownErrorMessage(body),
        code: body.error in errors ? body.error as ErrorCode : "unknown",
        params: body.params,
      };
    }
  } catch {
    /* gövde JSON değilse aşağıdaki genel mesaja düşer */
  }
  return { message: errors.generic(), code: "unknown" };
}

async function request(path: string, init: RequestInit, token: string | null): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body !== undefined) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // Arayüz siteden yüklendiğinde Origin site adresi olur; sunucu uygulamayı bununla tanıyor.
  const platform = nativePlatform();
  if (platform) headers.set("X-MK-App", platform);
  return fetch(`${apiOrigin()}${path}`, { ...init, headers, credentials: "same-origin" });
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await request(path, init, accessToken);

  // Jeton süresi dolmuşsa bir kez yenileyip tekrar dene.
  if (res.status === 401 && path !== "/api/auth/refresh") {
    const fresh = await refreshAccessToken();
    if (fresh) res = await request(path, init, fresh);
  }

  if (!res.ok) {
    const failure = await readError(res);
    throw new ApiError(failure.message, res.status, failure.code, failure.params);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return api<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
}
